<template>
	<v-dialog :model-value="absenceMenuStore.infoModal.isOpen" @update:model-value="(val) => !val && handleClose()" max-width="480">
		<v-card class="pa-2">
			<v-card-title class="d-flex align-center justify-space-between pa-4 pb-2">
				<span class="text-h6 font-weight-bold">{{ absence?.type ? t(`absence.type.${absence.type}`) : '' }}</span>
				<div class="icon-actions">
					<v-icon v-if="!isLocked" size="18" class="cursor-pointer" @click="handleDeleteClick"> mdi-delete </v-icon>
					<v-icon v-if="!isLocked" size="18" class="cursor-pointer" @click="handleEditFromInfo"> mdi-pencil </v-icon>
				</div>
			</v-card-title>

			<v-card-text class="pa-4 pt-2">
				<v-row no-gutters class="info-row">
					<v-col cols="6" class="d-flex">
						<span class="label">{{ t('absence.startDate') }}</span>
						<span class="value">{{ absence?.startDate ? formatDateByLocale(absence.startDate) : t('absence.not_available') }}</span>
					</v-col>
					<v-col cols="6" class="d-flex">
						<span class="label">{{ t('absence.endDate') }}</span>
						<span class="value">{{ absence?.endDate ? formatDateByLocale(absence.endDate) : t('absence.not_available') }}</span>
					</v-col>
				</v-row>

				<v-row no-gutters class="info-row">
					<v-col cols="6" class="d-flex">
						<span class="label">{{ t('absence.startTime') }}</span>
						<span class="value">{{ absence?.startTime ? formatTimeByLocale(absence.start) : t('absence.not_available') }}</span>
					</v-col>
					<v-col cols="6" class="d-flex">
						<span class="label">{{ t('absence.endTime') }}</span>
						<span class="value">{{ absence?.endTime ? formatTimeByLocale(absence.end) : t('absence.not_available') }}</span>
					</v-col>
				</v-row>

				<v-row no-gutters class="info-row">
					<v-col cols="12" class="d-flex">
						<span class="label">{{ t('setup.absence.status') }}</span>
						<span class="value">{{ absence?.status ? t(`absence.${absence.status}`) : t('absence.not_available') }}</span>
					</v-col>
				</v-row>

				<v-row no-gutters class="info-row" v-if="absence?.priority && (absence?.type === 'offtime' || absence?.type === 'reduce')">
					<v-col cols="12" class="d-flex">
						<span class="label">{{ t('absence.priority') }}</span>
						<span class="value">{{ t(`absence.priority.${priorityLabel(absence.priority)}`) }}</span>
					</v-col>
				</v-row>

				<v-row no-gutters class="info-row">
					<v-col cols="12" class="d-flex">
						<span class="label">{{ t('planning_notes') }}</span>
						<span class="value">{{ absence?.description || t('absence.not_available') }}</span>
					</v-col>
				</v-row>
			</v-card-text>

			<v-divider />

			<v-card-actions class="pa-4 justify-end">
				<v-btn variant="text" color="primary" class="font-weight-medium" @click="handleClose">{{ t('action_close') }}</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>

	<AbsenceDeleteConfirmDialog
		v-model="absenceMenuStore.deleteConfirm.isOpen"
		:loading="absenceMenuStore.deleteConfirm.deleting"
		@confirm="handleConfirmDelete"
		@cancel="absenceMenuStore.closeDeleteConfirm()"
	/>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { usePlanningAbsenceMenuStore } from '@/rennovation/planning-view/store/usePlanningAbsenceMenuStore';
import { useDateFormatter } from '@/rennovation/common/composables/use-date-formatter';
import { ApiAbsence } from '@/rennovation/planning-view/types/api/absence-responses';
import { useI18n } from 'vue-i18n';
import AbsenceDeleteConfirmDialog from '@/modules/users/components/dialogs/AbsenceDeleteConfirmDialog.vue';
const absenceMenuStore = usePlanningAbsenceMenuStore();
const { t } = useI18n();

const { formatTimeByLocale, formatDateByLocale } = useDateFormatter();

const absence = computed(() => absenceMenuStore.absenceList.items.find((a: ApiAbsence) => a.id === absenceMenuStore.infoModal.absenceId));
const isLocked = computed(() => !!absence.value && absenceMenuStore.isAbsenceLocked(absence.value));

const handleClose = () => {
	absenceMenuStore.infoModal.isOpen = false;
};

const handleEditFromInfo = () => {
	const id = absenceMenuStore.infoModal.absenceId;
	handleClose();
	if (id) absenceMenuStore.openEditModal(id);
};

const handleDeleteClick = () => {
	const id = absenceMenuStore.infoModal.absenceId;
	if (id) absenceMenuStore.openDeleteConfirm(id);
};

const handleConfirmDelete = (changeComment: string) => {
	absenceMenuStore.confirmDeleteAbsence(changeComment);
};

const priorityLabel = (priority: number) => {
	switch (priority) {
		case 1:
			return 'nice_to_have';
		case 2:
			return 'medium';
		case 3:
			return 'important';
		default:
			return null;
	}
};
</script>

<style scoped>
.info-row {
	margin-bottom: 16px;
}

.label {
	width: 96px;
	flex-shrink: 0;
	color: #6b7280;
	font-size: 14px;
	font-weight: 400;
}

.value {
	color: #000000;
	font-size: 14px;
	font-weight: 400;
}

.icon-actions {
	display: flex;
	align-items: center;
	gap: 12px;
}
</style>
